<template>
    <vx-expandable-card
        toggleable
        :active="currentStep === 'when'"
        class="relative border-t-0 overflow-hidden mt-8 origin-top-right"
        :class="{
            'animate-rip': open,
            'animate-replace': !open,
        }"
    >
        <template v-if="showForm" v-slot:header>
            <span
                class="pr-1.5 mr-2 border-y-0 border-l-0 border-r border-solid sm:pr-3 sm:mr-4"
                :class="{
                    'border-r-transparent': !canEditDate,
                    'border-r-slate-400': canEditDate,
                }"
            >
                Follow-up in...
            </span>
            <span v-if="canEditDate" class="text-xs font-normal text-slate-700 sm:text-base sm:font-semibold">
                {{ formattedFollowUpDate }}
            </span>
        </template>
        <template v-if="canEditDate" v-slot:headerRight>
            <vx-button @click.stop="currentStep = 'when'" color="muted" size="md">
                <span class="inline-block">Edit</span>
                <font-awesome-icon
                    widthAuto
                    class="text-base hidden! sm:inline-block!"
                    :icon="faPencil"
                ></font-awesome-icon>
            </vx-button>
        </template>
        <template v-slot:content>
            <div class="absolute inset-x-5 top-0 z-10 overflow-hidden">
                <svg
                    width="1495px"
                    height="5px"
                    viewBox="0 0 1495 5"
                    class="stroke-slate-300 transition -translate-y-1/2 delay-200"
                >
                    <line
                        x1="5"
                        x2="1495"
                        y1="2.5"
                        y2="2.5"
                        stroke-width="5"
                        stroke-linecap="round"
                        stroke-dasharray="0, 10"
                    ></line>
                </svg>
            </div>

            <animate-height :height="height">
                <div ref="contentDiv" class="w-full">
                    <quick-blast-follow-up-callout
                        v-if="!showForm"
                        @continue="open = true"
                        :success="success"
                    ></quick-blast-follow-up-callout>
                    <div v-if="showForm">
                        <quick-blast-follow-up-in
                            @cancel="open = false"
                            @continue="currentStep = 'message'"
                            :submiting="submiting"
                            :originalScheduledAt="props.originalScheduledAt"
                            v-model="followUpDate"
                            :formattedModelValue="formattedFollowUpDate"
                        ></quick-blast-follow-up-in>
                    </div>
                </div>
            </animate-height>
        </template>
    </vx-expandable-card>
    <quick-blast-follow-up-message
        v-if="showMessage"
        ref="followUpMessageComp"
        @fetched="onFollowUpMessagesFetched()"
        class="mt-8"
        :active="currentStep == 'message'"
        @update:active="$event ? (currentStep = 'message') : (currentStep = 'when')"
        @continue="createFollowUp()"
        :submiting="submiting"
        :originalCampaignId="props.originalCampaignId"
        :originalCampaignType="props.originalCampaignType"
        :originalMessage="props.originalMessage"
        v-model="message"
    ></quick-blast-follow-up-message>
</template>

<script setup>
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxExpandableCard } from '@voxie/frontend-components';
import { computed, onMounted, ref, watch } from 'vue';
import AnimateHeight from 'vue-animate-height';
import { getClientTz } from '../../utils/date';
import QuickBlastFollowUpIn from './QuickBlastFollowUpIn.vue';
import QuickBlastFollowUpMessage from './QuickBlastFollowUpMessage.vue';
import QuickBlastFollowUpCallout from './QuickBlastFollowUpCallout.vue';
import { campaignsClient } from '~/services';
import { useToasted } from '../../composables/useToasted';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    originalCampaignId: {
        type: Number,
        required: true,
    },
    originalCampaignType: {
        type: String,
        required: true,
    },
    originalCampaignCategory: {
        type: String,
        required: true,
    },
    originalCampaignName: {
        type: String,
        required: true,
    },
    originalScheduledAt: {
        type: Date,
        required: true,
    },
    originalMessage: {
        type: Object,
        required: true,
        default: () => ({}),
    },
});

const toasted = useToasted();

const currentStep = ref('when');

const open = ref(false);
const showForm = ref(false);
const showMessage = ref(false);
const height = ref('auto');
const contentDiv = ref(null);

const followUpId = ref(undefined);
const followUpName = computed(() => `${props.originalCampaignName} - Follow-Up`);

const followUpDate = ref();
const message = ref({
    body: '',
    mediaUrl: null,
    mediaContentType: null,
    selectedSnippetBody: null,
});

const success = ref(false);
const submiting = ref(false);

const followUpMessageComp = ref(null);

const createFollowUp = async () => {
    if (submiting.value) {
        return;
    }

    submiting.value = true;

    try {
        const response = await campaignsClient.campaignSave(teamId, {
            id: followUpId.value || undefined,
            name: followUpName.value,
            type: props.originalCampaignType,
            category: props.originalCampaignCategory,
            duration_type: 'one_time',
            original_campaign_id: props.originalCampaignId,
            remove_on_response: 1,
            remove_on_link_click: 1,
            live_at: toUTCDateTimeFormat(dayjs.tz(followUpDate.value, getClientTz())),
            campaign_messages: [
                {
                    body: message.value.body,
                    media_url: message.value.mediaUrl || null,
                    media_content_type: message.value.mediaContentType || null,
                    status: 'published',
                },
            ],
        });

        followUpId.value = response.data.id;
        await campaignsClient.changeStatus(teamId, followUpId.value, 'scheduled');
        success.value = true;
        open.value = false;

        currentStep.value = 'when';
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        submiting.value = false;
    }
};

const formattedFollowUpDate = computed(() => {
    if (!followUpDate.value) {
        return '';
    }

    const dayjsDate = dayjs(followUpDate.value);
    const tz = dayjsDate.tz(getClientTz()).format('z');
    return dayjsDate.tz(getClientTz()).format('MMMM Do, YYYY [at] h:mm a') + ' ' + tz;
});

const canEditDate = computed(() => {
    return currentStep.value != 'when' && followUpDate.value;
});

const setupResizer = () => {
    const resizeObserver = new ResizeObserver(() => {
        height.value = contentDiv.value.clientHeight;
    });

    resizeObserver.observe(contentDiv.value);
};
onMounted(() => {
    setupResizer();
});

watch(canEditDate, () => {
    if (!canEditDate.value) {
        setTimeout(() => {
            setupResizer();
        }, 100);
    }
});

watch(open, (newVal) => {
    if (newVal) {
        setTimeout(() => {
            showForm.value = true;
        }, 350);

        setTimeout(() => {
            showMessage.value = true;
        }, 500);
    } else {
        showForm.value = false;
        showMessage.value = false;
    }
});

const onFollowUpMessagesFetched = () => {
    if (followUpId.value && followUpDate.value) {
        submiting.value = false;
        currentStep.value = 'message';
    }
};
</script>
